<template>
  <a 
    :class="['media-item', { 
      'media-item--selected': selected,
      'media-item--allowed': allowed,
      'media-item--not-moderated': notModerated,
      'media-item--disabled': disabled,
    }]"
    :href="profileMedia.url" 
    @click.prevent="onClick"
  >
    <div v-if="bulkIsActive && !disabled" class="media-item__top-left-slot">
      <svg-profile-media-selected v-if="selected" />
    </div>
    <div class="media-item__top-right-slot">
      <svg-mcv-verified v-if="allowed" />
      <svg-profile-media-not-moderated v-if="notModerated" />
    </div>
    <div v-if="placeholder" class="media-item__placeholder">
      <svg-profile-media-placeholder  />
    </div>
    <img
      v-else-if="isImage" 
      :src="profileMedia.url" 
      class="media-item__img" 
      @error="handleImageError" 
    />
  </a>
</template>

<script>
export default {
  components: {
    SvgProfileMediaSelected: () => import(/* webpackChunkName: "chunk-svg-profile-media-selected" */ '../../../svg/profile-media-selected.vue'),
    SvgProfileMediaNotModerated: () => import(/* webpackChunkName: "chunk-svg-profile-media-not-moderated" */ '../../../svg/profile-media-not-moderated.vue'),
    SvgMcvVerified: () => import(/* webpackChunkName: "chunk-svg-mcv-verified" */ '../../../svg/mcv-verified.vue'),
    SvgProfileMediaPlaceholder: () => import(/* webpackChunkName: "chunk-svg-profile-media-placeholder" */ '../../../svg/profile-media-placeholder.vue'),
  },
  props: {
    profileMedia: {
      type: Object,
      required: true,
      validator(value) {
        const validTypes = ['TYPE_IMAGE'];
        const hasValidType = validTypes.includes(value.type);
        const hasUrl = typeof value.url === 'string';

        if (!hasValidType) {
          console.error(`Invalid media type: ${value.type}. Expected one of: ${validTypes.join(', ')}`);
        }

        if (!hasUrl) {
          console.error(`Invalid or missing 'url' property. Expected a string.`);
        }

        return hasValidType && hasUrl;
      },
    },
    bulkIsActive: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    forceAllowed: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customClick: {
      type: Function
    },
    index: {
      type: Number,
    },
  },
  data() {
    return {
      placeholder: false,
      profileMediaStatuses: Object.freeze({
        ALLOWED: 'allowed',
        STATUS_NOT_MODERATED: 'not_moderated',
        STATUS_MODERATED:'moderated',
        STATUS_NOT_ALLOWED: 'not_allowed'
      }),
    }
  },
  computed: {
    isImage() {
      return this.profileMedia.type === 'TYPE_IMAGE';
    },
    allowed() {
      return this.profileMedia.status === this.profileMediaStatuses.ALLOWED || this.forceAllowed;
    },
    notModerated() {
      return this.profileMedia.status === this.profileMediaStatuses.STATUS_NOT_MODERATED;
    }
  },
  methods: {
    handleImageError() {
      this.placeholder = true;
    },
    onClick(event) {
      if(this.disabled) {
        return;
      }

      if (this.bulkIsActive) {
        this.customClick(this.profileMedia);
      }

      this.$emit('click', event, this.profileMedia)
    }
  }
};
</script>

<style>
.media-item {
  width: 100%;
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 16px;
  background: var(--color-light);
  z-index: 10;
}
.media-item.media-item--not-moderated {
  outline: 1px solid var(--color-warning);
}
.media-item.media-item--allowed {
  outline: 1px solid var(--color-profile-media-allowed);
  color: var(--color-secondary);
}
.media-item.media-item--selected {
  outline: 2px solid var(--color-profile-media-selected);
}
.media-item.media-item--disabled {
  outline: 1px solid var(--color-profile-media-allowed);
  cursor: not-allowed;
}
.media-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(43, 43, 43, 0.4);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 5;
}
.media-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-light);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 5;
}
.media-item.media-item--disabled::after,
.media-item.media-item--disabled .media-item__top-right-slot {
  opacity: .7; /* Show background on disabled */
}
.media-item.media-item--selected::before {
  opacity: 1; /* Show background on selected */
}
.media-item__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Ensures the image covers the container */
  min-height: 105px;
  max-height: 180px;
  max-width: 180px;
}
.media-item__top-left-slot {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 10;
}
.media-item__top-right-slot {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 1;
}
.media-item__placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}
</style>
